import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import { EntityChartMapCleanUpService } from './entity-chart-map-clean-up.service';

@Component({
    selector: 'gtw-entity-chart-map-clean-up',
    templateUrl: './entity-chart-map-clean-up.component.html',
    styleUrls: ['./entity-chart-map-clean-up.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EntityChartMapCleanUpComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  public floatingConfig = {
    isFloating: true
  }
  public minimise = false;
  baseURLs: any = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  gridData: GridType[] = [];
  baseGlobalParams: any = {};

  isLoading = true;
  enableButton: boolean = false;

  clob_data: GridData[] = [];

  allEntityDetails: GridData[] = [];
  optionsTransCount: Array<TransCountType> = [];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private entityChartMapCleanUpService: EntityChartMapCleanUpService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.baseGlobalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    const gd = gridPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(gd) && gd.length > 0) {
      this.gridData = gd;
    }
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.baseURLs?.api) return;
    if (!this.baseGlobalParams?.jcd_key) return;
    if (!Array.isArray(this.gridData) || this.gridData.length === 0) return;
    this.isInitialized = true;
    this.getEntityData();
  }

  expandRow(index: number) {
    this.allEntityDetails.forEach((elem: GridData) => {
      if (this.allEntityDetails[index].INPUT_LE_KEY == elem.INPUT_LE_KEY) {
        elem.display = true;
      }
    });
    this.allEntityDetails[index].PLUS = false;
  }

  collapseRow(index: number) {
    this.allEntityDetails.forEach((elem: GridData, idx: number) => {
      if (index !== idx && this.allEntityDetails[index].INPUT_LE_KEY == elem.INPUT_LE_KEY) {
        elem.display = false;
      }
    });
    this.allEntityDetails[index].PLUS = true;
  }

  getEntityData(): void {
    let params = {
      tax_year: this.baseGlobalParams?.tax_year,
      scenario: this.baseGlobalParams?.scenario,
      jcd_key: this.baseGlobalParams?.jcd_key
    }
    let clob_data: GridData[] = [];
    this.gridData.forEach((current: GridType) => {
      let temp = {
        LE_KEY: current.data.LE_KEY,
        LE_TYPE: current.data.LE_TYPE
      }
      clob_data.push(temp);
    })

    this.entityChartMapCleanUpService.getEntityDetails(this.baseURLs.api, params, clob_data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      let allData: GridData[] = data.jsonObject;
      if (allData) {
        let allLEKeys: { LE_KEY?: string, AGG_COMBINATION_KEY?: string }[] = [];
        allData.forEach((value: GridData, index: number) => {

          allLEKeys.push({ LE_KEY: value.LE_KEY, AGG_COMBINATION_KEY: value.AGG_COMBINATION_KEY });
          if (value.IS_PARENT === 'N') {
            let msg = '';
            if (value.IS_CKB_DISABLED === 'Y') {
              msg = 'This entity cannot be selected for deletion as it is parent to another entity!';
            }
            else if (value.TP_KEY_DTLS_COUNT != 0 || value.PSHIP_DTLS_COUNT != 0) {
              msg = 'Partnership/Trading Partner Details found, cannot delete!';
            }
            else {
              msg = 'This entity can be selected for deletion!';
            }
            this.allEntityDetails.push({ ...value, display: false, message: msg, PLUS: false });
          } else {
            this.allEntityDetails.push({ ...value, display: true, message: 'Click plus to expand', PLUS: true });
          }
        });
        this.allEntityDetails.sort((a: GridData, b: GridData) => {
          if (a.INPUT_LE_KEY == b.INPUT_LE_KEY) {
            return b.display - a.display;
          }
          return a.INPUT_LE_KEY > b.INPUT_LE_KEY ? 1 : -1;
        })
        this.entityChartMapCleanUpService.getTransCount(this.baseURLs.api, this.baseGlobalParams?.tax_year, this.baseGlobalParams?.scenario, this.baseGlobalParams?.jcd_key, allLEKeys).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
          this.optionsTransCount = data;

          this.isLoading = false;
        });
      }
      else {
        this.isLoading = false;
      }

    }, () => {
      console.log("Error getting data");
    });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  selectEntity(event: any, item: GridData) {
    if (event.checkboxState) {
      this.clob_data.push(item);
    }
    else {
      this.clob_data = this.clob_data.filter((data: GridData) =>
        !(item.AGG_COMBINATION_KEY == data.AGG_COMBINATION_KEY && item.LE_KEY == data.LE_KEY)
      );
    }

    if (this.clob_data.length > 0) {
      this.enableButton = true;
    } else {
      this.enableButton = false;
    }
  }

  deleteEntities(): void {
    this.isLoading = true;
    let params = {
      tax_year: this.baseGlobalParams?.tax_year,
      scenario: this.baseGlobalParams?.scenario,
      jcd_key: this.baseGlobalParams?.jcd_key,
      scenarioCode: this.baseGlobalParams?.scenarioCode,
    }

    this.entityChartMapCleanUpService.deleteEntities(this.baseURLs.api, params, this.clob_data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data !== 'error') {
        this.requestGridUpdate.emit('refresh');
        this.isLoading = false;
      }
      else {
        console.log("Error while saving");
        this.isLoading = false;
      }

    }, () => {
      console.log("Error while saving");
    });
  }



}
export type GridData = any;

export type GridType = {
  data: GridData;
}

export type TransCountType = {
  LE_KEY: string;
  TRANS_DETAILS: string;
  TRANS_TYPE: string;
  TRANS_TYPE_COUNT: number;
  TRANS_TYPE_DESC: string;
  TRANS_TYPE_KEY: number;
  AGG_COMBINATION_KEY: string;
}
